import React from "react";
import { ChevronRight, RefreshCw } from "lucide-react";
import { Card, MetricTitle, BigNumber, DeltaChip } from "./ui";
import type { KpiScope } from "./kpiScope";
import type { Variant } from "../data/data";

export type MetricKey =
  | "active"
  | "time"
  | "proficiency"
  | "hours"
  | "lessons"
  | "teachers";

export const METRIC_LABELS: Record<MetricKey, string> = {
  active: "Active Students",
  time: "Average Time per Day",
  proficiency: "On track to year end proficiency",
  hours: "Tutoring hours used",
  lessons: "Lessons completed",
  teachers: "Active teachers",
};

const fmt = (n: number) => n.toLocaleString("en-US");

function MetricCard({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`Open ${label} details`}
      className="w-full cursor-pointer border-none bg-transparent p-0 text-left"
    >
      <Card className="flex h-full flex-col gap-2.5 p-5">{children}</Card>
    </button>
  );
}

function Title({
  children,
  info,
}: {
  children: React.ReactNode;
  info?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <MetricTitle info={info}>{children}</MetricTitle>
      <ChevronRight
        size={17}
        strokeWidth={2.4}
        className="shrink-0 text-[#28543D]"
      />
    </div>
  );
}

const ACTIVE_INFO =
  "Active = a student who spent at least 4 hours learning in the last 30 days. Lifetime = students who have ever spent 4+ hours total (students who actually used the platform, not accounts created). Fixed 30-day window, not affected by the filters below.";
const TIME_INFO =
  "Average minutes of learning per student per day, averaged over the last 30 days (total time ÷ 30). Framed per day so it reads directly without mentally dividing a 30-day total. The secondary figure is all-time hours across every student. Fixed 30-day window, not affected by the filters below.";
const TRACK_INFO =
  "Share of students on track to reach grade-level proficiency by the end of the year, based on skills / learning components mastered versus the number expected at this point in the school calendar. Only students meeting the minimum usage bar (4+ hrs in the last 30 days) are included. Refreshed monthly.";

export function MetricOverview({
  scope,
  variant,
  onSelect,
}: {
  scope: KpiScope;
  variant: Variant;
  onSelect: (metric: MetricKey) => void;
}) {
  const hoursUsed = variant.engagement.tutoringHoursNum;
  const activeTeachers = variant.teachers.filter(
    (t) => t.activity >= 60,
  ).length;
  const activeTeacherPct =
    variant.teachers.length > 0
      ? Math.round((activeTeachers / variant.teachers.length) * 100)
      : 0;

  return (
    <div className="grid grid-cols-3 gap-3.5">
      <MetricCard
        label={METRIC_LABELS.active}
        onClick={() => onSelect("active")}
      >
        <Title info={ACTIVE_INFO}>{METRIC_LABELS.active}</Title>
        <BigNumber className="text-[32px]">
          {scope.activePct}%
          <span className="text-base text-[#687368]"> active</span>
        </BigNumber>
        <span className="text-[12.5px] text-[#687368]">
          <strong className="font-bold text-[#294B39]">
            {fmt(scope.activeCount)}
          </strong>{" "}
          active out of{" "}
          <strong className="font-bold text-[#294B39]">
            {fmt(scope.lifetime)}
          </strong>{" "}
          lifetime students
        </span>
        <DeltaChip
          delta={scope.activeTrend}
          suffix="%"
          compareLabel="vs prior 30 days"
        />
      </MetricCard>

      <MetricCard label={METRIC_LABELS.time} onClick={() => onSelect("time")}>
        <Title info={TIME_INFO}>{METRIC_LABELS.time}</Title>
        <BigNumber className="text-[32px]">
          {scope.avgMin}
          <span className="text-[17px] text-[#687368]"> min</span>
        </BigNumber>
        <span className="text-[12.5px] text-[#687368]">
          per student per day, last 30 days
        </span>
        <DeltaChip
          delta={scope.avgTrend}
          suffix="%"
          compareLabel="vs prior 30 days"
        />
      </MetricCard>

      <MetricCard
        label={METRIC_LABELS.proficiency}
        onClick={() => onSelect("proficiency")}
      >
        <Title info={TRACK_INFO}>{METRIC_LABELS.proficiency}</Title>
        <BigNumber className="text-[32px]">{scope.onTrackPct}%</BigNumber>
        <div className="flex items-center gap-2">
          <RefreshCw
            size={13}
            strokeWidth={2.25}
            className="shrink-0 text-[#294B39]"
          />
          <div className="flex items-baseline gap-1.5 leading-[1.25]">
            <span className="text-[12.5px] font-bold text-[#294B39]">
              Last updated July 2026
            </span>
            <span className="text-[11.5px] font-semibold text-[#687368]">
              Refreshed monthly
            </span>
          </div>
        </div>
        <DeltaChip
          delta={scope.onTrackTrend}
          suffix="%"
          compareLabel="vs last month"
        />
      </MetricCard>

      <MetricCard label={METRIC_LABELS.hours} onClick={() => onSelect("hours")}>
        <Title info="Total 1:1 AI-tutor hours consumed against the hours contracted for the year.">
          {METRIC_LABELS.hours}
        </Title>
        <BigNumber className="text-[32px]">
          {fmt(hoursUsed)}
          <span className="text-base text-[#687368]"> hrs</span>
        </BigNumber>
        <span className="text-[12.5px] text-[#687368]">
          tutoring hours used
        </span>
        <DeltaChip delta={6} suffix="%" compareLabel="vs prior 30 days" />
      </MetricCard>

      <MetricCard
        label={METRIC_LABELS.lessons}
        onClick={() => onSelect("lessons")}
      >
        <Title info="Total lessons students have finished, including the assessed check for understanding.">
          {METRIC_LABELS.lessons}
        </Title>
        <BigNumber className="text-[32px]">
          {fmt(variant.lessons.completed)}
        </BigNumber>
        <span className="text-[12.5px] text-[#687368]">lessons completed</span>
        <DeltaChip delta={7} suffix="%" compareLabel="vs prior 30 days" />
      </MetricCard>

      <MetricCard
        label={METRIC_LABELS.teachers}
        onClick={() => onSelect("teachers")}
      >
        <Title info="Share of teachers in the current selection actively using the platform in the period.">
          {METRIC_LABELS.teachers}
        </Title>
        <BigNumber className="text-[32px]">
          {activeTeacherPct}%
          <span className="text-base text-[#687368]"> active</span>
        </BigNumber>
        <span className="text-[12.5px] text-[#687368]">
          <strong className="font-bold text-[#294B39]">{activeTeachers}</strong>{" "}
          active out of{" "}
          <strong className="font-bold text-[#294B39]">
            {variant.teachers.length}
          </strong>{" "}
          teachers
        </span>
        <DeltaChip delta={-2} suffix="%" compareLabel="vs prior 30 days" />
      </MetricCard>
    </div>
  );
}
